import React from 'react'
import dayjs, { Dayjs } from 'dayjs'
import styles from '../../Calendar.module.scss'
import { MonthProps } from '~types'
import { DayjsInput } from 'utils/dayjs'
import CalendarTopNavigation from '../../elements/CalendarTopNavigation'
import CalendarBottomNavigation from '../../elements/CalendarBottomNavigation'
import { ArtistProps } from '../../types'
import CalendarTabletView from '../../elements/CalendarTabletView'
import { useWindowSize } from 'utils/hooks'
import MonthViewHeader from '../MonthView/MonthViewHeader/MonthViewHeader'
import CalendarDesktopView from '../../elements/CalendarDesktopView'
import { EventsByDate, FormattedEvent } from '../../types/Event'

type CalendarViewProps = {
  selectedMonth: MonthProps
  activeMonthList: string[]
  setSelectedDate: (date: DayjsInput) => void
  toggleView?: React.ReactNode
  handleDateSelection: (event: FormattedEvent[], artist: ArtistProps) => void
  eventsByDate: EventsByDate | null
  artists: ArtistProps[]
}

export const CalendarView = React.memo(
  ({
    eventsByDate,
    setSelectedDate,
    selectedMonth,
    activeMonthList,
    toggleView,
    handleDateSelection,
    artists
  }: CalendarViewProps) => {
    const { isBreakpoint: isBelowMd } = useWindowSize('md', '<')

    // Handle month navigation
    const handleNavigation = (key: string) => {
      const monthFormat = 'MMM-YYYY' // matches activeMonthList strings

      const currentIndex = activeMonthList.findIndex(m =>
        dayjs(m, monthFormat).isSame(dayjs(selectedMonth.value, 'MMMM-YYYY'), 'month')
      )

      if (currentIndex === -1) return

      if (key === 'next' && currentIndex < activeMonthList.length - 1) {
        setCurrentMonth(dayjs(activeMonthList[currentIndex + 1], monthFormat))
      } else if (key === 'previous' && currentIndex > 0) {
        setCurrentMonth(dayjs(activeMonthList[currentIndex - 1], monthFormat))
      } else if (key === 'today') {
        const today = dayjs().startOf('month')
        const todayIndex = activeMonthList.findIndex(m => dayjs(m, monthFormat).isSame(today, 'month'))

        if (todayIndex !== -1) {
          setCurrentMonth(dayjs(activeMonthList[todayIndex], monthFormat))
        }
      }
    }

    // Function to set the current month
    const setCurrentMonth = (month: Dayjs) => {
      setSelectedDate(dayjs(month, 'MMMM-YYYY').startOf('month').toDate())
    }

    //Set Selected Date to States
    const setSelectedMonth = (month: MonthProps) => {
      setSelectedDate(dayjs(month.value, 'MMMM-YYYY').startOf('month').toDate())
    }

    if (!eventsByDate || Object.keys(eventsByDate).length === 0) return null

    return (
      <div className={styles['calendar__months']}>
        <CalendarTopNavigation
          selectedMonth={selectedMonth}
          activeMonthList={activeMonthList}
          setSelectedMonth={setSelectedMonth}
          toggleView={toggleView}
          handleNavigation={handleNavigation}
          isTablet={isBelowMd}
        />
        <div className={styles['calendar__month']}>
          {!isBelowMd && <MonthViewHeader />}
          {eventsByDate &&
            Object.keys(eventsByDate)?.length > 0 &&
            (isBelowMd ? (
              <CalendarTabletView
                handleDateSelection={handleDateSelection}
                eventsByDate={eventsByDate}
                artists={artists}
                month={dayjs(selectedMonth?.value, 'MMMM-YYYY')}
              />
            ) : (
              <CalendarDesktopView
                handleDateSelection={handleDateSelection}
                eventsByDate={eventsByDate}
                artists={artists}
                month={dayjs(selectedMonth?.value, 'MMMM-YYYY')}
              />
            ))}
        </div>
        <CalendarBottomNavigation
          hideNavTitle={true}
          month={selectedMonth?.value ? dayjs(selectedMonth?.value, 'MMMM-YYYY') : dayjs()}
          handleNavigation={handleNavigation}
          activeMonthList={activeMonthList}
          toggleView={toggleView}
        />
      </div>
    )
  }
)
